import { defaultDatabaseDirectory } from "@-/com.npmjs.expo-sqlite";
import { Directory } from "@-/com.npmjs.expo-file-system";

/**
 * Resolve the exact on-disk path expo-sqlite uses for `<name>` so every engine
 * (expo-sqlite for content/static, Turso for the actor database) and every file
 * operation (wipe, relocate) agree on ONE file.
 *
 * Do not probe with `openDatabaseAsync()` here. On Android that creates a
 * SQLite-header file before Turso `connect()` runs, which turns a fresh install
 * into a "non-empty file" that skips bootstrap. A pure string join has no side
 * effects and matches expo-sqlite's own `${directory}/${name}` resolution.
 */
export function resolveSqlitePath(databaseName: string): string {
  // `defaultDatabaseDirectory` is `ExpoSQLite.defaultDatabaseDirectory`, which is UNDEFINED
  // wherever the native module is absent — notably the web export's SSR prerender, which bundles
  // with `resolver.environment=node` and therefore takes the `default` (native) side of every
  // platform-split export rather than the `browser` one. Reading `.startsWith` off it crashed the
  // whole `expo export --platform web` with "Cannot read properties of undefined (reading
  // 'startsWith')" (run 35764113666), which then SKIPPED every maestro shard, since they gate on
  // `web-preview-deploy`. expo's own `pathUtils` guards the same value, so throwing a named error
  // beats a TypeError from deep inside a minified bundle.
  const defaultDirectory: string | undefined = defaultDatabaseDirectory;
  if (defaultDirectory === undefined) {
    throw new Error(
      `Cannot resolve a SQLite path for "${databaseName}": expo-sqlite has no defaultDatabaseDirectory on this platform (web/SSR). Use the browser-condition module instead.`,
    );
  }
  // Turso's native normalizer only recognizes paths starting with `/`. Passing
  // Expo's iOS file URI makes it prepend its own directory to `file://...`.
  const directory = defaultDirectory.startsWith("file://")
    ? decodeURIComponent(defaultDirectory.slice("file://".length))
    : defaultDirectory;
  const separator = directory.endsWith("/") ? "" : "/";
  return `${directory}${separator}${databaseName}`;
}

/** Create only the parent directory; never create a SQLite file before Turso bootstrap. */
export function ensureSqliteDirectory(): void {
  new Directory(toFileUri(defaultDatabaseDirectory)).create({
    idempotent: true,
    intermediates: true,
  });
}

/**
 * expo-file-system's `File` constructor requires an ABSOLUTE `file://` URI. iOS
 * paths already come back as `file://…`, but Android hands back a bare filesystem
 * path (`/data/data/<pkg>/files/SQLite/actor.db`) and passing that throws
 * `java.lang.IllegalArgumentException: URI is not absolute` — which once made every
 * sidecar delete on sign-in silently fail on Android, so the account's cloud deck
 * never downloaded. Normalise before touching the file system.
 */
export function toFileUri(path: string): string {
  return path.startsWith("file://") ? path : `file://${path}`;
}
